/** Who wrote a message: the merchant, the support agent, or the Goodchart team. */
export type AgentMessageRole = 'user' | 'assistant' | 'admin';

/** Who is answering: the agent, or the Goodchart team once a person is involved (GOO-465). */
export type AgentConversationStatus = 'agent' | 'needs_follow_up' | 'with_admin' | 'resolved';

export type AgentFeedbackRating = 'up' | 'down';

/** The merchant's rating of an agent reply. */
export interface AgentMessageFeedback {
  rating: AgentFeedbackRating;
  comment?: string;
}

export interface AgentMessage {
  /** Missing on a message only just sent, until the conversation is reloaded. */
  _id?: string;
  role: AgentMessageRole;
  content: string;
  /** The tools the agent used for this reply; only on `assistant` messages. */
  tools?: string[];
  /** The merchant's rating; only on `assistant` messages. */
  feedback?: AgentMessageFeedback;
  createdAt?: string;
}

/** A conversation in the history list. */
export interface AgentConversationSummary {
  _id: string;
  title: string;
  status: AgentConversationStatus;
  updatedAt: string;
  /** The Goodchart team replied since the merchant last opened it. */
  unread: boolean;
}

export interface AgentConversation extends Omit<AgentConversationSummary, 'unread'> {
  messages: AgentMessage[];
}

/** `GET agent/status`: whether the agent answers, and the launcher's badge. */
export interface AgentChatStatus {
  enabled: boolean;
  unreadCount: number;
}

/**
 * One event of a turn (the `back` merchant chat API, GOO-455): the agent's reply as it streams, or
 * the message stored for the team when a person is handling the conversation.
 */
export type AgentChatEvent =
  | { type: 'start'; conversationId: string }
  | { type: 'text'; delta: string }
  | { type: 'tool'; name: string }
  | { type: 'human-requested'; summary: string }
  | { type: 'done'; conversationId: string; messageId: string }
  | { type: 'error'; message: string }
  | { type: 'stored'; conversationId: string; message: AgentMessage };

/** A turn the API refused before it started: the limits, the agent being off, or anything else. */
export class AgentChatError extends Error {
  constructor(
    readonly status: number,
    /** The API's error code, such as `AGENT_RATE_LIMITED`; empty when it sent none. */
    readonly code: string,
    /** From `Retry-After`, on the limits. */
    readonly retryAfterSeconds?: number,
  ) {
    super(code || `HTTP ${status}`);
  }
}
